import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ExportToCsv } from 'export-to-csv';

import { getFormattedCancelStatusFromDrive, getFormattedStatusFromDrive } from '@services/statusService';
import { capFirst, getCsvMonetaryValue } from '@services/formatService';

import {
  buildPerMoveLines,
  condensePerUseCharges,
  describePerUseLine,
  type PerMoveBuckets,
  type PerMoveLine,
  type PerMoveOtherLine,
} from '@hopdrive/sdk-ar';
import type { GqlExecutor } from '@hopdrive/sdk-core';

import {
  ChargesByMoveGroup,
  formatAddressShort,
  groupChargesByMove,
} from '@features/invoices/services/calcServiceCharges';
import { getAmountDueExecutor } from '@features/invoices/services/amountDueExecutor';
import {
  GetInvoiceEnhancedCsvChargesDocument,
  type GetInvoiceEnhancedCsvChargesQuery,
  type GetInvoiceEnhancedCsvChargesQueryVariables,
} from '@features/invoices/queries/getInvoiceEnhancedCsvCharges.query.generated';
import {
  GetInvoiceEnhancedCsvMovesDocument,
  type GetInvoiceEnhancedCsvMovesQuery,
  type GetInvoiceEnhancedCsvMovesQueryVariables,
} from '@features/invoices/queries/getInvoiceEnhancedCsvMoves.query.generated';

dayjs.extend(utc);

// Earliest billable_datetime across a group's charges — used as the row's date
const getGroupDate = (charges: any[] = []): string | null => {
  const stamps = charges.map(c => c.billable_datetime).filter(Boolean);
  if (!stamps.length) return null;
  const earliest = stamps.reduce((min, s) => (new Date(s) < new Date(min) ? s : min));
  return dayjs.utc(earliest).format('MM/DD/YYYY');
};

// Lane/route description for a group (move lane, or guestride pickup → dropoff)
const getGroupLane = (group: Pick<ChargesByMoveGroup, 'sourceKind' | 'guestride' | 'move'>): string => {
  if (group.sourceKind === 'guestride') {
    const pickup = formatAddressShort(group.guestride?.pickup_address);
    const dropoff = formatAddressShort(group.guestride?.dropoff_address);
    return pickup && dropoff ? `${pickup} to ${dropoff}` : pickup || dropoff || 'Unknown Lane';
  }
  return group.move?.lane?.description || 'Unknown Lane';
};

const getCsvOptions = (invoice: any) => ({
  filename: `${invoice?.customer?.name?.replace(/ /g, '_')}_Invoice_${dayjs(invoice.start_datetime).add(1, 'day').format('MM-DD-YYYY')}_to_${dayjs(invoice.end_datetime).format('MM-DD-YYYY')}`,
  showTitle: true,
  title: `Invoice #${invoice.id}`,
  useKeysAsHeaders: true,
});

/**
 * Standard CSV for charge-based (new AR) invoices — one row per move/guestride group.
 * Mirrors the legacy generateCSV columns, adapted to the argroups/charges shape.
 */
export const generateChargesCSV = (invoice: any) => {
  if (!invoice) return;

  const argroups = invoice.argroups || [];
  const moveGroups = groupChargesByMove(argroups);

  const moveRows = moveGroups.map(group => {
    const isGuestride = group.sourceKind === 'guestride';
    const move = group.move;
    const guestride = group.guestride;

    return {
      TYPE: isGuestride ? 'Guestride' : 'Move',
      ID: isGuestride ? `R-#${guestride?.id}` : `M-#${move?.id}`,
      REF_NUM: (isGuestride ? guestride?.reference_num : move?.reference_num) || '-',
      VIN: (!isGuestride && move?.vehicle_vin) || '-',
      MOVE_DATE: getGroupDate(group.charges) || '-',
      STOCK: !isGuestride && move?.vehicle_stock ? move.vehicle_stock.toUpperCase() : 'N/A',
      LANE: getGroupLane(group),
      DISTANCE: !isGuestride && move?.lane?.distance_miles ? `${move.lane.distance_miles} mi` : '-',
      COST: getCsvMonetaryValue(Math.max(group.total, 0)),
      STATUS: isGuestride
        ? capFirst(guestride?.status) || '-'
        : move?.cancel_status
          ? getFormattedCancelStatusFromDrive(move)
          : move?.status
            ? getFormattedStatusFromDrive(move)
            : '-',
      DISTANCE_MILES: (!isGuestride && move?.lane?.distance_miles) || null,
    };
  });

  // Per-use fees print as one condensed row, the line every renderer shows.
  const perUse = condensePerUseCharges(argroups);
  const perUseRows = perUse
    ? [
        {
          TYPE: 'Per Use',
          ID: '-',
          REF_NUM: '-',
          VIN: '-',
          MOVE_DATE: '-',
          STOCK: 'N/A',
          LANE: describePerUseLine(perUse),
          DISTANCE: '-',
          COST: getCsvMonetaryValue(perUse.amount),
          STATUS: '-',
          DISTANCE_MILES: null,
        },
      ]
    : [];

  const csvExporter = new ExportToCsv(getCsvOptions(invoice));
  csvExporter.generateCsv([...moveRows, ...perUseRows]);
};

/** Moves read per request when the Enhanced CSV loads its move records. */
export const MOVE_ID_BATCH_SIZE = 500;

type EnhancedCsvCharge = GetInvoiceEnhancedCsvChargesQuery['archarges'][number];
type EnhancedCsvMove = GetInvoiceEnhancedCsvMovesQuery['moves'][number];
type EnhancedCsvGuestride = NonNullable<EnhancedCsvCharge['guestride']>[number];

/** The invoice fields an Enhanced CSV row writes. */
type EnhancedCsvInvoice = { id: number | string; customer?: { name?: string | null } | null };

const bucketColumns = (buckets: PerMoveBuckets) => ({
  TRANSPORT_TOTAL: buckets.transport.total,
  FUEL_TOTAL: buckets.fuel.total,
  FUEL_COUNT: buckets.fuel.count,
  SURGE_TOTAL: buckets.surge.total,
  SURGE_COUNT: buckets.surge.count,
  PARTIAL_TOTAL: buckets.partial.total,
  PARTIAL_COUNT: buckets.partial.count,
  WAIVED_TOTAL: buckets.waived.total,
  WAIVED_COUNT: buckets.waived.count,
  TOW_TOTAL: buckets.tow.total,
  TOW_COUNT: buckets.tow.count,
  DELAY_TOTAL: buckets.delay.total,
  DELAY_COUNT: buckets.delay.count,
  RIDE_TOTAL: buckets.ride.total,
  RIDE_COUNT: buckets.ride.count,
});

const formatBillableDate = (stamp: string | null): string => (stamp ? dayjs.utc(stamp).format('MM/DD/YYYY') : '-');

const moveRow = (invoice: EnhancedCsvInvoice, line: PerMoveLine, move: EnhancedCsvMove | undefined) => ({
  ROOFTOP: move?.customer?.name || invoice?.customer?.name || '-',
  INVOICE_ID: invoice?.id || '-',
  MOVE_ID: line.moveId,
  REF_NUM: move?.reference_num || '-',
  VIN: move?.vehicle_vin || '-',
  MOVE_DATE: formatBillableDate(line.billableDatetime),
  STOCK: move?.vehicle_stock ? move.vehicle_stock.toUpperCase() : 'N/A',
  LANE: move?.lane?.description || 'Unknown Lane',
  DISTANCE: move?.lane?.distance_miles ? `${move.lane.distance_miles} mi` : '-',
  COST: getCsvMonetaryValue(line.net),
  ...bucketColumns(line.buckets),
  CANCEL_STATUS: move?.cancel_status ? `${getFormattedCancelStatusFromDrive(move as never)}` : '-',
  MOVE_STATUS: move?.status ? `${getFormattedStatusFromDrive(move as never)}` : '-',
  DISTANCE_MILES: move?.lane?.distance_miles || '-',
});

/** What an other line's LANE says, since it has no lane: the guest ride's route, the product, the fee. */
const describeOtherLine = (line: PerMoveOtherLine, guestride: EnhancedCsvGuestride | undefined): string => {
  switch (line.kind) {
    case 'guestride':
      return `Guest ride: ${getGroupLane({ sourceKind: 'guestride', guestride, move: null })}`;
    case 'product':
      return `Product: ${line.name || '-'}`;
    case 'perUse':
      return `${line.name}: ${line.count} charge${line.count === 1 ? '' : 's'}`;
    default:
      return 'Charges not tied to a move';
  }
};

const otherRow = (invoice: EnhancedCsvInvoice, line: PerMoveOtherLine, guestride: EnhancedCsvGuestride | undefined) => ({
  ROOFTOP: invoice?.customer?.name || '-',
  INVOICE_ID: invoice?.id || '-',
  MOVE_ID: '-',
  REF_NUM: guestride?.reference_num || '-',
  VIN: '-',
  MOVE_DATE: formatBillableDate(line.billableDatetime),
  STOCK: '-',
  LANE: describeOtherLine(line, guestride),
  DISTANCE: '-',
  COST: getCsvMonetaryValue(line.net),
  ...bucketColumns(line.buckets),
  CANCEL_STATUS: '-',
  MOVE_STATUS: '-',
  DISTANCE_MILES: '-',
});

async function loadMoves(executor: GqlExecutor, moveIds: Array<string | number | bigint>): Promise<EnhancedCsvMove[]> {
  const batches: Array<Array<string | number | bigint>> = [];
  for (let i = 0; i < moveIds.length; i += MOVE_ID_BATCH_SIZE) batches.push(moveIds.slice(i, i + MOVE_ID_BATCH_SIZE));
  const results = await Promise.all(
    batches.map(batch =>
      executor.query<GetInvoiceEnhancedCsvMovesQuery, GetInvoiceEnhancedCsvMovesQueryVariables>(
        GetInvoiceEnhancedCsvMovesDocument,
        { moveIds: batch as number[] },
      ),
    ),
  );
  return results.flatMap(result => result?.moves ?? []);
}

/**
 * Enhanced CSV for charge-based (new AR) invoices, in the AR v1 file's layout:
 * one row per vehicle move with per-type totals and counts, then a row each
 * for guest rides, products and the per-use fees in the same columns, so the
 * COST column adds up to the invoice.
 *
 * It reads the invoice's charges flat rather than from the invoice page's tree,
 * which loads a move record under every charge, and reads each move once. The
 * reads skip the Apollo cache: a thousand-move answer used once would make
 * Apollo re-read every watched query it touched.
 *
 * @throws when a read fails, before any file is written.
 */
export const generateChargesEnhancedCSV = async (
  invoice: (EnhancedCsvInvoice & { start_datetime?: string | null; end_datetime?: string | null }) | null | undefined,
  executor: GqlExecutor | undefined = getAmountDueExecutor(),
) => {
  if (!invoice) return;
  if (!executor) throw new Error('Enhanced CSV: no GraphQL executor registered');

  const { archarges = [], arcredits = [] } =
    (await executor.query<GetInvoiceEnhancedCsvChargesQuery, GetInvoiceEnhancedCsvChargesQueryVariables>(
      GetInvoiceEnhancedCsvChargesDocument,
      { invoiceId: Number(invoice.id) },
    )) ?? {};

  // Placed once without the move records to learn which moves to read, then
  // again with them, which only changes the leg a scenario-level charge takes.
  const unanchored = buildPerMoveLines({ charges: archarges, credits: arcredits });
  const moves = await loadMoves(executor, unanchored.moves.map(line => line.moveId));
  const lines = buildPerMoveLines({ charges: archarges, credits: arcredits, moves });

  const movesById = new Map(moves.map(move => [String(move.id), move]));
  const guestridesById = new Map(
    archarges.flatMap(charge => charge.guestride ?? []).map(guestride => [String(guestride.id), guestride]),
  );

  const csvRows = [
    ...lines.moves.map(line => moveRow(invoice, line, movesById.get(String(line.moveId)))),
    ...lines.others.map(line =>
      otherRow(invoice, line, line.kind === 'guestride' ? guestridesById.get(String(line.sourceId)) : undefined),
    ),
  ];

  const csvExporter = new ExportToCsv(getCsvOptions(invoice));
  csvExporter.generateCsv(csvRows);
};
